
import React, { memo } from "react";
import { Button } from "../button";
import { WarningIcon, RefreshIcon, LockClosedIcon, DatabaseIcon } from "@/icons";

interface ModalLoadingStateProps {
  title?: string;
  description?: string;
}

export const ModalLoadingState = memo(function ModalLoadingState({
  title = "Đang tải...",
  description,
}: ModalLoadingStateProps) {
  return (
    <div className="py-12 flex flex-col items-center justify-center">
      <div className="relative">
        <div className="size-10 border-3 border-gray-200 dark:border-gray-700 rounded-full" />
        <div className="absolute inset-0 size-10 border-3 border-transparent border-t-brand-500 rounded-full animate-spin" />
      </div>
      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{title}</p>
      {description && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{description}</p>
      )}
    </div>
  );
});

interface ModalErrorStateProps {
  title?: string;
  error: string | null;
  onRetry?: () => void;
  retryLabel?: string;
}

export const ModalErrorState = memo(function ModalErrorState({
  title = "Không thể kết nối",
  error,
  onRetry,
  retryLabel = "Thử lại",
}: ModalErrorStateProps) {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20">
        <WarningIcon className="size-6 text-red-500" />
      </div>
      <h3 className="text-sm font-medium text-gray-800 dark:text-white/90">{title}</h3>
      {error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">{error}</p>
      )}
      {onRetry && (
        <div className="mt-4">
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RefreshIcon className="size-4 mr-1.5" />
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
});

interface ModalNoAccessStateProps {
  title?: string;
  description?: string;
  onClose?: () => void;
  closeLabel?: string;
}

export const ModalNoAccessState = memo(function ModalNoAccessState({
  title = "Không có quyền truy cập",
  description = "Bạn chưa được cấp quyền truy cập. Vui lòng liên hệ admin để được cấp quyền.",
  onClose,
  closeLabel = "Đóng",
}: ModalNoAccessStateProps) {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-900/20">
        <LockClosedIcon className="size-6 text-amber-500" />
      </div>
      <h3 className="text-sm font-medium text-gray-800 dark:text-white/90">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
        {description}
      </p>
      {onClose && (
        <div className="mt-4">
          <Button size="sm" variant="outline" onClick={onClose}>
            {closeLabel}
          </Button>
        </div>
      )}
    </div>
  );
});

interface ModalEmptyStateProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}

export const ModalEmptyState = memo(function ModalEmptyState({
  title = "Không tìm thấy dữ liệu",
  description,
  icon,
}: ModalEmptyStateProps) {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
        {icon || <DatabaseIcon className="size-6 text-gray-400" />}
      </div>
      <h3 className="text-sm font-medium text-gray-800 dark:text-white/90">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );
});

// Small loading state for inline use
export const ModalSmallLoading = memo(function ModalSmallLoading({
  title = "Đang tải...",
}: { title?: string }) {
  return (
    <div className="py-8 flex flex-col items-center justify-center">
      <div className="relative">
        <div className="size-8 border-2 border-gray-200 dark:border-gray-700 rounded-full" />
        <div className="absolute inset-0 size-8 border-2 border-transparent border-t-brand-500 rounded-full animate-spin" />
      </div>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{title}</p>
    </div>
  );
});

// Simple error state for inline use
export const ModalSmallError = memo(function ModalSmallError({
  error,
}: { error: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-sm text-red-500">{error}</p>
    </div>
  );
});

// Simple empty state for inline use
export const ModalSmallEmpty = memo(function ModalSmallEmpty({
  message,
}: { message: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  );
});
